
import { useState, useEffect } from 'react';
import { AutomationRule, ValidationResult } from '../types/automationTypes';
import { validateAutomationRule } from '../schemas/automationRuleSchema';

export const useAutomationRuleValidation = (rule?: AutomationRule) => {
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    warnings: [],
  });
  
  const [isValidating, setIsValidating] = useState(false);

  const validateRule = async (ruleData: unknown): Promise<ValidationResult> => {
    setIsValidating(true);
    
    try {
      const validation = validateAutomationRule(ruleData);
      
      const result: ValidationResult = {
        isValid: validation.success,
        errors: validation.errors || [],
        warnings: [],
      };

      // Add business logic warnings
      if (validation.success && validation.data) {
        const warnings: string[] = [];
        
        // Check for potential performance issues
        if (validation.data.actions.length > 3) {
          warnings.push('Consider reducing the number of actions for better performance');
        }
        
        // Check for high-frequency triggers
        if (validation.data.trigger.type === 'time_based') {
          warnings.push('Time-based triggers may execute frequently - ensure this is intended');
        }
        
        // Check for overly broad conditions
        if (validation.data.trigger.type === 'email_received' && 
            Object.keys(validation.data.trigger.conditions).length === 0) {
          warnings.push('Email trigger without conditions will match all emails');
        }

        result.warnings = warnings;
      }

      setValidationResult(result);
      return result;
    } catch (error) {
      const errorResult: ValidationResult = {
        isValid: false,
        errors: ['Validation failed due to unexpected error'],
        warnings: [],
      };
      setValidationResult(errorResult);
      return errorResult;
    } finally {
      setIsValidating(false);
    }
  };

  // Validate on rule change
  useEffect(() => {
    if (rule) {
      validateRule(rule);
    }
  }, [rule]);

  const hasErrors = validationResult.errors.length > 0;
  const hasWarnings = validationResult.warnings.length > 0;

  return {
    validationResult,
    isValidating,
    validateRule,
    hasErrors,
    hasWarnings,
    isValid: validationResult.isValid,
  };
};
